import { useEffect, useLayoutEffect, useReducer, useRef, useState, type PointerEvent as ReactPointerEvent, type RefObject } from 'react';

import { indicatorBox, isNewScreen, navDirection, nextPresence, prefersReducedMotion, shouldDismissSheet, type NavDirection, type Presence } from './motion';

/**
 * Keeps an overlay mounted while it animates out. `phase` goes to the
 * element as data-phase: CSS gives 'opening' and 'closing' the hidden pose
 * and 'open' the resting one, so every change is a transition that can turn
 * around mid-way.
 */
export function usePresence(open: boolean, { exitMs }: { exitMs: number }): { mounted: boolean; phase: Presence } {
  const [phase, dispatch] = useReducer(nextPresence, 'closed');

  useEffect(() => {
    dispatch(open ? 'show' : 'hide');
  }, [open]);

  useEffect(() => {
    if (phase === 'opening') {
      // Two frames: the hidden pose has to be painted once before the resting one, or nothing moves.
      let inner = 0;
      const outer = requestAnimationFrame(() => {
        inner = requestAnimationFrame(() => dispatch('settled'));
      });
      return () => {
        cancelAnimationFrame(outer);
        cancelAnimationFrame(inner);
      };
    }
    if (phase === 'closing') {
      const timer = setTimeout(() => dispatch('settled'), prefersReducedMotion() ? 0 : exitMs);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [phase, exitMs]);

  // The first render after `open` turns true is already 'opening': the overlay mounts in its hidden pose at once.
  const shown: Presence = open && phase === 'closed' ? 'opening' : phase;
  return { mounted: shown !== 'closed', phase: shown };
}

/**
 * Drag down to dismiss, for sheets that rise from the bottom. While dragging,
 * the sheet follows the finger through --drag-y with data-dragging (CSS drops
 * the transition); on release it closes when the flick is fast or long enough
 * (shouldDismissSheet), and otherwise springs back. Only on touch and pen, and
 * never from a control, so taps on links and buttons stay taps.
 */
export function useSheetDrag(sheet: RefObject<HTMLElement | null>, onDismiss: () => void) {
  const start = useRef<{ y: number; t: number; id: number } | null>(null);

  const release = (element: HTMLElement) => {
    element.removeAttribute('data-dragging');
    element.style.removeProperty('--drag-y');
  };

  return {
    onPointerDown(event: ReactPointerEvent<HTMLElement>) {
      if (event.pointerType === 'mouse' || (event.target as HTMLElement).closest('a, button, input, select, textarea, [role="menuitem"]')) return;
      start.current = { y: event.clientY, t: performance.now(), id: event.pointerId };
    },
    onPointerMove(event: ReactPointerEvent<HTMLElement>) {
      const element = sheet.current;
      if (!start.current || !element || event.pointerId !== start.current.id) return;
      const distance = Math.max(0, event.clientY - start.current.y);
      if (distance > 4 && !element.hasAttribute('data-dragging')) {
        element.setAttribute('data-dragging', '');
        element.setPointerCapture?.(event.pointerId);
      }
      if (element.hasAttribute('data-dragging')) element.style.setProperty('--drag-y', `${distance}px`);
    },
    onPointerUp(event: ReactPointerEvent<HTMLElement>) {
      const element = sheet.current;
      const begun = start.current;
      start.current = null;
      if (!begun || !element || !element.hasAttribute('data-dragging')) return;
      const distance = event.clientY - begun.y;
      const dismiss = shouldDismissSheet({ distance, elapsedMs: performance.now() - begun.t, size: element.getBoundingClientRect().height });
      release(element);
      if (dismiss) onDismiss();
    },
    onPointerCancel() {
      start.current = null;
      if (sheet.current) release(sheet.current);
    },
  };
}

/**
 * The entrance of each new screen: which way it leans and a key that flips
 * between 'a' and 'b' so the same CSS animation starts again without
 * remounting the page.
 */
export function useScreenEntrance(pathname: string, order: readonly string[]): { direction: NavDirection; key: 'a' | 'b' } {
  const [entrance, setEntrance] = useState<{ path: string; direction: NavDirection; key: 'a' | 'b' }>({ path: pathname, direction: 'none', key: 'a' });
  if (isNewScreen(entrance.path, pathname)) {
    setEntrance({ path: pathname, direction: navDirection(order, entrance.path, pathname), key: entrance.key === 'a' ? 'b' : 'a' });
  }
  return { direction: entrance.direction, key: entrance.key };
}

/**
 * A sliding highlight behind the active item of a row of tabs: writes the
 * item's box to --ind-x, --ind-y, --ind-w and --ind-h on the container. The
 * first measure only places it (data-indicator="ready" turns the slide on
 * after), so it never flies in from the corner. It measures again when the
 * container resizes or its items change.
 */
export function useActiveIndicator(container: RefObject<HTMLElement | null>, activeSelector: string, activeKey: unknown): void {
  const measured = useRef(false);

  useLayoutEffect(() => {
    const element = container.current;
    if (!element) return undefined;

    const place = () => {
      const active = element.querySelector<HTMLElement>(activeSelector);
      if (!active) {
        element.dataset.indicator = 'none';
        measured.current = false;
        return;
      }
      const box = indicatorBox(element.getBoundingClientRect(), active.getBoundingClientRect());
      element.style.setProperty('--ind-x', `${box.x}px`);
      element.style.setProperty('--ind-y', `${box.y}px`);
      element.style.setProperty('--ind-w', `${box.w}px`);
      element.style.setProperty('--ind-h', `${box.h}px`);
      element.dataset.indicator = measured.current ? 'ready' : 'placed';
      if (!measured.current) {
        measured.current = true;
        // The first box is placed without a slide; the next frame allows sliding.
        if (typeof requestAnimationFrame === 'function') requestAnimationFrame(() => (element.dataset.indicator = 'ready'));
        else element.dataset.indicator = 'ready';
      }
    };

    place();
    // Links can arrive after the page (the plan, the tools): a change in the items measures again.
    const mutations = typeof MutationObserver === 'function' ? new MutationObserver(place) : undefined;
    mutations?.observe(element, { childList: true, subtree: true });
    const resizes = typeof ResizeObserver === 'function' ? new ResizeObserver(place) : undefined;
    resizes?.observe(element);
    return () => {
      mutations?.disconnect();
      resizes?.disconnect();
    };
  }, [container, activeSelector, activeKey]);
}
